import { queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api, ApiError } from '@/lib/api'
import { reportError } from '@/lib/settings'

export const CATEGORIES = [
  ['terms', 'Terms and conditions'],
  ['insurance', 'Insurance certificates'],
  ['policy', 'Policies'],
  ['method_statement', 'Method statements'],
  ['certificate', 'Certificates and accreditations'],
  ['other', 'Other documents'],
] as const
export type Category = (typeof CATEGORIES)[number][0]
export const categoryLabel = (c: Category) => CATEGORIES.find(([k]) => k === c)?.[1] ?? c

export const ACCEPT = '.pdf,.docx,.doc,.xlsx,.png,.jpg,.jpeg,.webp'
export const MAX_BYTES = 25 * 1024 * 1024

export interface LibraryVersion {
  id: string
  documentId: string
  versionNo: number
  originalName: string
  size: number
  mime: string
  sha256: string
  expiresOn: string | null
  notes: string
  uploadedBy: string
  createdAt: string
}
export interface LibraryDocument {
  id: string
  title: string
  category: Category
  description: string
  includeByDefault: boolean
  active: boolean
  sort: number
  version: number
  usedInSentQuote: boolean
  updatedAt: string
  current: LibraryVersion | null
  versions: number
}

export const libraryQuery = queryOptions({
  queryKey: ['library'],
  queryFn: async () =>
    (await api.get<{ items: LibraryDocument[] }>('/library?includeArchived=true')).items,
})

export const historyQuery = (id: string) =>
  queryOptions({
    queryKey: ['library', id, 'history'],
    queryFn: () => api.get<LibraryDocument & { history: LibraryVersion[] }>(`/library/${id}`),
  })

export const fileUrl = (docId: string, versionId: string, download = false) =>
  `/api/v1/library/${docId}/versions/${versionId}/file${download ? '?download=1' : ''}`

/**
 * Multipart upload with progress (fetch cannot report upload progress).
 * Same-origin, cookie-authenticated; errors use the API's stable shape.
 */
export function upload<T>(
  path: string,
  form: FormData,
  onProgress?: (fraction: number) => void,
): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('POST', `/api/v1${path}`)
    xhr.withCredentials = true
    xhr.responseType = 'json'
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress?.(e.loaded / e.total)
    xhr.onerror = () =>
      reject(
        new ApiError(
          0,
          'network',
          'The upload was interrupted. Check your connection and try again.',
        ),
      )
    xhr.onload = () => {
      const body = xhr.response
      if (xhr.status >= 200 && xhr.status < 300) resolve(body as T)
      else
        reject(
          new ApiError(
            xhr.status,
            body?.error?.code ?? 'unknown',
            body?.error?.message ?? 'The upload failed.',
          ),
        )
    }
    xhr.send(form)
  })
}

async function deleteDocument(id: string): Promise<void> {
  const res = await fetch(`/api/v1/library/${id}`, {
    method: 'DELETE',
    credentials: 'same-origin',
  }).catch(() => {
    throw new ApiError(
      0,
      'network',
      'You appear to be offline. Check your connection and try again.',
    )
  })
  if (res.status === 204) return
  const body = await res.json().catch(() => null)
  throw new ApiError(
    res.status,
    body?.error?.code ?? 'unknown',
    body?.error?.message ?? 'The document could not be deleted.',
  )
}

export function useLibraryMutations() {
  const qc = useQueryClient()
  const refresh = () => void qc.invalidateQueries({ queryKey: ['library'] })
  const patch = useMutation({
    mutationFn: ({
      id,
      ...body
    }: { id: string; expectedVersion: number } & Record<string, unknown>) =>
      api.patch<LibraryDocument>(`/library/${id}`, body),
    onSuccess: (doc, vars) => {
      refresh()
      toast.success(
        vars.active === false
          ? `${doc.title} archived`
          : vars.active === true
            ? `${doc.title} restored`
            : 'Document saved',
      )
    },
    onError: (e) => reportError(e, refresh),
  })
  const remove = useMutation({
    mutationFn: (doc: LibraryDocument) => deleteDocument(doc.id),
    onSuccess: (_, doc) => {
      refresh()
      toast.success(`${doc.title} deleted`)
    },
  })
  return { patch, remove, refresh }
}

export function formatBytes(n: number) {
  if (n < 1024) return `${n} B`
  if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
  return `${(n / 1024 / 1024).toFixed(1)} MB`
}

export function fileKind(mime: string) {
  if (mime === 'application/pdf') return 'PDF'
  if (mime.startsWith('image/')) return mime.split('/')[1]!.toUpperCase().replace('JPEG', 'JPG')
  if (mime.includes('spreadsheet')) return 'Excel'
  if (mime.includes('word')) return 'Word'
  return 'File'
}

export const formatDate = (iso: string) =>
  new Intl.DateTimeFormat('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }).format(
    new Date(iso.length === 10 ? `${iso}T12:00:00Z` : iso),
  )

export function expiryState(expiresOn: string | null, now = new Date()) {
  if (!expiresOn) return null
  const end = new Date(`${expiresOn}T23:59:59`)
  const days = Math.ceil((end.getTime() - now.getTime()) / 86_400_000)
  if (days < 0) return { tone: 'expired' as const, label: `Expired ${formatDate(expiresOn)}` }
  if (days <= 30)
    return {
      tone: 'soon' as const,
      label: days === 0 ? 'Expires today' : `Expires in ${days} day${days === 1 ? '' : 's'}`,
    }
  return { tone: 'ok' as const, label: `Expires ${formatDate(expiresOn)}` }
}
